import { Button } from "@/components/ui/button";
import { Flame, Clock } from "lucide-react";
import { useCountdown } from "@/hooks/useCountdown";
import footballLogo from "@/assets/football-logo.webp";

interface StartScreenProps {
  onStart: () => void;
  error?: string | null;
  streak?: number;
  alreadyPlayed?: boolean;
}

const StartScreen = ({ onStart, error, streak = 0, alreadyPlayed }: StartScreenProps) => {
  const { hours, minutes, seconds } = useCountdown();

  return (
    <main className="animate-fade-in w-full max-w-4xl px-1 text-center">
      {/* Logo */}
      <div className="mb-5 flex justify-center">
        <img
          src={footballLogo}
          alt="Daily Football Quiz logo"
          width={120}
          height={120}
        />
      </div>

      <section className="mx-auto max-w-3xl">
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Daily Football Quiz – 10 Questions. New Every Day.
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
          Test your football knowledge across the Premier League, Champions League, international football and more. Can you score 10/10?
        </p>
      </section>

      {streak > 0 && (
        <div className="mt-5 flex w-fit mx-auto items-center justify-center gap-2">
          <Flame size={20} className="text-primary" />
          <span className="font-heading font-semibold text-foreground">
            {streak} day streak!
          </span>
        </div>
      )}

      {error && (
        <p className="mb-4 mt-4 text-sm text-destructive">{error}</p>
      )}

      {!alreadyPlayed && (
        <section className="mt-7">
          <Button
            size="lg"
            className="min-h-14 px-10 py-6 text-lg font-heading font-semibold"
            onClick={onStart}
          >
            Start Today’s Quiz
          </Button>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            No sign-up required • Takes 2–3 minutes • Challenge your friends
          </p>
        </section>
      )}

      {alreadyPlayed && (
        <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm mt-4">
          <Clock size={16} />
          <span>
            Next quiz in{" "}
            <span className="font-heading font-semibold text-foreground">
              {String(hours).padStart(2, "0")}:{String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </span>
          </span>
        </div>
      )}

      <section className="mx-auto mt-10 max-w-3xl border-t border-border/50 pt-8">
        <p className="text-sm font-medium uppercase tracking-normal text-muted-foreground">
          Join thousands of football fans testing their knowledge every day.
        </p>
      </section>

      <section className="mx-auto mt-10 max-w-4xl text-left">
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-foreground sm:text-3xl">How It Works</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <article className="rounded-md border border-border bg-card p-5 text-card-foreground">
              <p className="text-sm font-medium text-muted-foreground">Step 1</p>
              <p className="mt-2 text-base leading-7">Answer 10 football questions</p>
            </article>
            <article className="rounded-md border border-border bg-card p-5 text-card-foreground">
              <p className="text-sm font-medium text-muted-foreground">Step 2</p>
              <p className="mt-2 text-base leading-7">See your score instantly</p>
            </article>
            <article className="rounded-md border border-border bg-card p-5 text-card-foreground">
              <p className="text-sm font-medium text-muted-foreground">Step 3</p>
              <p className="mt-2 text-base leading-7">Share your result and challenge your friends</p>
            </article>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-4xl text-left">
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-foreground sm:text-3xl">Why Play the Daily Football Quiz?</h2>
          <ul className="grid gap-4 text-base leading-7 text-muted-foreground md:grid-cols-2">
            <li className="rounded-md border border-border bg-card p-5 text-card-foreground">New quiz every single day</li>
            <li className="rounded-md border border-border bg-card p-5 text-card-foreground">Covers all areas of football (Premier League, UCL, transfers, history)</li>
            <li className="rounded-md border border-border bg-card p-5 text-card-foreground">Quick and fun – perfect for a break or commute</li>
            <li className="rounded-md border border-border bg-card p-5 text-card-foreground">Compete with friends and improve your knowledge</li>
          </ul>
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-4xl text-left">
        <div className="space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-foreground sm:text-3xl">Explore More Football Quizzes</h2>
          <nav aria-label="Explore more football quizzes">
            <ul className="grid gap-4 md:grid-cols-3">
              <li>
                <a href="/football-quiz" className="block rounded-md border border-border bg-card p-5 text-card-foreground transition-colors hover:border-primary hover:text-primary">
                  Ultimate Football Quiz
                </a>
              </li>
              <li>
                <a href="/premier-league-quiz" className="block rounded-md border border-border bg-card p-5 text-card-foreground transition-colors hover:border-primary hover:text-primary">
                  Premier League Quiz
                </a>
              </li>
              <li>
                <a href="/hard-football-quiz" className="block rounded-md border border-border bg-card p-5 text-card-foreground transition-colors hover:border-primary hover:text-primary">
                  Hard Football Quiz
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-4xl border-t border-border/50 pt-8 text-left">
        <p className="text-base leading-8 text-muted-foreground">
          This daily football quiz is designed for fans of all levels. Whether you follow the Premier League, Champions League, or international football, you’ll find a mix of questions to challenge your knowledge across every part of the game. Come back every day for a fresh set of 10 questions, compare your latest score, and see how you stack up against friends. If you enjoy a fast football quiz during a break, want a competitive daily football quiz to share, or just like testing yourself with football trivia and the occasional soccer quiz challenge, this is an easy way to play regularly and keep improving.
        </p>
      </section>

      <section className="mx-auto mt-8 max-w-4xl text-left">
        <p className="text-sm leading-6 text-muted-foreground">
          Looking for more? Try the <a href="/" className="text-primary underline underline-offset-4">daily football quiz</a> or explore more football quiz pages above.
        </p>
      </section>

      <div className="mt-6">
        <a
          href="/admin/review"
          className="text-xs text-muted-foreground/60 hover:text-muted-foreground underline-offset-4 hover:underline"
        >
          Admin
        </a>
      </div>
    </main>
  );
};

export default StartScreen;
